// Dedicated parser for web-search citations and <thinking> tag handling.
// To be implemented: Extract logic from useChatStream.ts

export interface WebSearchStreamCallbacks {
  onCitations?: (citations: Array<{ url: string; title: string; snippet?: string }>) => void;
  onThinkingChunk?: (chunk: string) => void;
  onContentChunk?: (chunk: string) => void;
  onResponseId?: (id: string) => void;
}
/**
 * Processes a buffer of streamed content for web-search events.
 * Handles citations, <thinking> tag, response ID markers, and regular content,
 * invoking callbacks. Unknown JSON objects are silently discarded to prevent
 * backend event markers from leaking into the visible chat response.
 *
 * @param buffer - The string buffer to process
 * @param callbacks - Callback functions for citations, thinking, response ID, and content events
 */
export function parseWebSearchStream(
  buffer: string,
  callbacks: WebSearchStreamCallbacks = {}
) {
  // Split buffer into lines, keeping each line's trailing newline so that
  // markdown structure (headers, blank lines, etc.) is preserved when the
  // fragments are later accumulated into the response string.
  const lines = buffer.split(/(?<=\n)/g);
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    // Try to parse as JSON — only JSON-looking lines need the attempt.
    let parsed: any = null;
    if (trimmed.startsWith('{')) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        parsed = null;
      }
    }
    if (parsed !== null) {
      // Capture backend response ID for session continuity
      if (parsed.__response_id) {
        callbacks.onResponseId?.(parsed.__response_id);
        continue;
      }
      // Handle citations event
      if (parsed.__web_search_citations === true && Array.isArray(parsed.citations)) {
        callbacks.onCitations?.(parsed.citations);
        continue;
      }
      // Handle thinking event
      if (parsed.__web_search_thinking === true && typeof parsed.content === 'string') {
        callbacks.onThinkingChunk?.(parsed.content);
        continue;
      }
      // Unknown JSON marker — silently discard so it never reaches the UI
      continue;
    }
    // Non-JSON line: pass the raw `line` (not `trimmed`) to preserve
    // trailing newlines that are required for correct markdown rendering.
    callbacks.onContentChunk?.(line);
  }
}

